/**
 * session.ts — the PHARMACY session store.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: pharmacy dispensing lane
 * (2026-07-29).
 *
 * WHY this is separate from `auth/session.ts` rather than another role on it:
 *
 *  - DIFFERENT FRONT DOOR. A pharmacist has no password lane. They sign in at
 *    /auth-pharmacy with a single-use emailed link token AND their personal
 *    code, and identity answers with the principal itself (pharmacistUserId +
 *    pharmacyOrgId). There is no `/auth/me` to re-hydrate from, so the console
 *    keeps the principal it was handed instead of re-deriving one.
 *  - NO CROSS-CONTAMINATION. Sharing `gps.session` would mean a pharmacy token
 *    could satisfy `RequireRole` on a patient/staff route, and a pharmacy 401
 *    would clear a patient's session (and bounce them to /auth) in another tab.
 *    Two keys, two stores, no overlap in either direction.
 *
 * The stored value is the session token plus the principal — ids and an expiry
 * only, no PHI (R19). `expiresAt` is enforced on READ: an expired record reads
 * as signed-out and is dropped, so a stale token is never put on the wire. Every
 * storage access is guarded so private mode / SSR never crashes, matching
 * auth/session.ts.
 *
 * NOTE ON PERSISTENCE: this survives a reload on purpose — the link token is
 * single-use, so a pharmacist who refreshed would otherwise need a fresh email
 * to get back into the queue. The mitigation for a shared dispensary
 * workstation is not a short-lived tab session but the step-up code the
 * contract requires on the dispensing action itself, which re-proves the
 * individual at the moment attribution is recorded. That code is checked on the
 * server (orders-service asks identity-service) since 2026-09-11; before then
 * only its shape was validated, so this mitigation did not yet exist.
 *
 * WHICH STORE (Clinics-in-Core, 2026-09-07): localStorage when standalone — the
 * reload survival above, unchanged. sessionStorage on the Core-hosted image, the
 * same selection auth/session.ts makes and for the same reason: that build shares
 * an ORIGIN with a GPS Core portal, and a Clinics bearer must not sit in that
 * origin's localStorage. Belt-and-braces only — the pharmacy console is a
 * link-out to the standalone portal from Core (CTO decision), so no emailed link
 * points here; but the routes exist on that image, so the store follows the rule.
 */
import { isCoreHosted } from '../config';
import type { PharmacyAuthResult } from '../api/types';

const STORAGE_KEY = 'gps.pharmacy.session';

/** The store for THIS build — see "WHICH STORE" above. */
function store(): Storage {
  return isCoreHosted() ? sessionStorage : localStorage;
}

/** The stored principal — what the console needs to work, and nothing more. */
export interface PharmacySession {
  sessionToken: string;
  pharmacistUserId: string;
  pharmacyOrgId: string;
  expiresAt: string;
  fullName?: string | null;
}

/** Subscribers notified when the pharmacy session is cleared (401 or sign-out). */
const listeners = new Set<() => void>();

/**
 * Read the stored pharmacy session, or null when signed out.
 *
 * An expired record is DROPPED here rather than merely ignored — leaving it in
 * storage would keep a dead credential on the machine for no benefit.
 */
export function getPharmacySession(): PharmacySession | null {
  let raw: string | null = null;
  try {
    raw = store().getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: Partial<PharmacySession>;
  try {
    parsed = JSON.parse(raw) as Partial<PharmacySession>;
  } catch {
    // Corrupt/hand-edited value — treat as signed out and bin it.
    clearPharmacySession();
    return null;
  }
  if (!parsed.sessionToken || !parsed.pharmacyOrgId || !parsed.pharmacistUserId || !parsed.expiresAt) {
    clearPharmacySession();
    return null;
  }
  if (Date.parse(parsed.expiresAt) <= Date.now()) {
    clearPharmacySession();
    return null;
  }
  return parsed as PharmacySession;
}

/** The bearer for a pharmacy call, or null when signed out/expired. */
export function getPharmacyToken(): string | null {
  return getPharmacySession()?.sessionToken ?? null;
}

/** Persist the session identity established at /auth-pharmacy. */
export function setPharmacySession(result: PharmacyAuthResult): void {
  const session: PharmacySession = {
    sessionToken: result.sessionToken,
    pharmacistUserId: result.pharmacistUserId,
    pharmacyOrgId: result.pharmacyOrgId,
    expiresAt: result.expiresAt,
    fullName: result.fullName ?? null,
  };
  try {
    store().setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Best-effort — private mode simply will not survive a reload.
  }
}

/** Remove the stored pharmacy session (sign-out, expiry, 401). */
export function clearPharmacySession(): void {
  try {
    store().removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
}

/** Subscribe to pharmacy sign-out events; returns an unsubscribe fn. */
export function onPharmacyUnauthorized(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Clear the pharmacy session and notify subscribers (called on a 401). */
export function notifyPharmacyUnauthorized(): void {
  clearPharmacySession();
  for (const listener of listeners) listener();
}
